import utils from 'utils/utils';
import { Model } from 'utils/model';
import device from 'utils/device';
import ServerTime from 'utils/server-time';
import config from 'player/config';
import {
    OVERRIDE_SLOT_PARAM,
    OVERRIDE_SLOT_TAG_ID,
} from 'player/playback/ads/appnexus-jw-config';
import type { Captions, ParsedAsset } from 'player/model/asset';
import type { Asset, Nullable } from '@schibsted-svp/svp-api-types';

type Stream = Omit<ParsedAsset, 'images' | 'assetType' | 'access'> & {
    access: Nullable<string>;
    hasSubtitlesInHls: string;
    showAds?: boolean;
    disableNextVideo: boolean;
    type: ParsedAsset['streamType'] | 'mock';
    videoPreviews: Record<string, string>;
    chunkDuration: number;
    captions: Captions[];
    playAhead: number;
    tts: boolean;
    isPodcast: boolean;
    mediaType: ParsedAsset['assetType'];
    externalId?: Nullable<string>;
    playlist: Asset[];
    streams: {
        hls: string;
        hlsdrm?: string;
        mp4: string[];
    };
    images: {
        main: Nullable<string>;
        snapshots: Nullable<string>;
    };
};

export class StreamModel extends Model<Stream> {
    constructor(options: ParsedAsset) {
        super({ id: 0 });
        this.initialize(options);
    }

    initialize(data: ParsedAsset) {
        Object.assign(this.attributes, this.parse(data));
    }

    /**
     * Parse data from asset
     * Be aware, data went through AssetModel.parse method
     */
    parse(data: ParsedAsset) {
        const result: Partial<Stream> = {
            id: data.id,
            vendor: data.vendor,
            title: data.title,

            streams: {
                hls: data.streamUrls!.hls,
                mp4: data.streamUrls!.mp4 ? [data.streamUrls!.mp4] : [],
            },
            status: data.status,
            type: data.streamType,
            mediaType: data.assetType,
            streamConfiguration: data.streamConfiguration,

            images: {
                main: data.images!.main,
                snapshots: data.images!.snapshots || null,
            },

            captions: data.captions || [],

            flightTimes: {
                start: null,
                end: null,
            },

            playback: {
                begin: null,
                end: null,
            },

            externalId: (data.externalId || null) as string | undefined,
            category: data.category,
        };

        if (data.duration) {
            result.duration = data.duration / 1000;
        }

        if (data.additional && data.additional.metadata) {
            result.disableNextVideo = data.additional.metadata
                .disableNextVideo as unknown as boolean;
            result.chunkDuration = Number(
                data.additional.metadata.chunkDuration,
            );
            result.isPodcast = data.additional.metadata.isPodcast === 'true';
            result.hasSubtitlesInHls =
                data.additional.metadata.hasSubtitlesInHls;
            result.tts = Boolean(data.additional.metadata.tts_articleId);

            result.videoPreviews = Object.entries(
                data.additional.metadata,
            ).reduce((videoPreviews, [metadataKey, metadataValue]) => {
                if (!metadataKey.includes('preview')) {
                    return videoPreviews;
                }
                return {
                    ...videoPreviews,
                    [metadataKey]: metadataValue,
                };
            }, {});

            if (result.streams && config.drmSystems.enabled) {
                result.streams.hlsdrm = data.additional.metadata.drmPlaybackUrl;
            }
        }

        // override start and end if set in api
        if (data.flightTimes) {
            result.flightTimes = {
                start: data.flightTimes.start ? data.flightTimes.start : null,
                end: data.flightTimes.end ? data.flightTimes.end : null,
            };
        }

        if (data.playback) {
            result.playback = {
                begin: data.playback.begin,
                end: data.playback.end,
            };
        }

        // additional stream settings
        if (data.settings) {
            result.showAds = data.settings.showAds;
        }

        // cue points, empty array if nothing set
        result.cuePoints = data.cuePoints;

        // playlist, empty array if nothing set
        result.playlist = data.playlist || [];

        // add midroll in 10s TODO remove after tests
        // result.cuePoints = [31];
        return result as Stream;
    }

    getId(): number {
        return this.get('id');
    }

    getVendor(): string {
        return this.get('vendor');
    }

    getCategory() {
        return this.get('category');
    }

    hasProperty(property: Stream['streamConfiguration']['properties'][number]) {
        return (
            this.get('streamConfiguration').properties.indexOf(property) > -1
        );
    }

    hasSubtitlesInHls() {
        return this.get('hasSubtitlesInHls') === 'true';
    }

    /**
     * Get duration stored in API
     */
    getDuration() {
        return this.get('duration');
    }

    getCuePoints() {
        return this.get('cuePoints');
    }

    /**
     * Check if stream has Akamai Secure Token
     */
    isSecure() {
        return this.hasProperty('tokenSecured');
    }

    /**
     * Check if stream has DRM enabled
     */
    isDrmProtected() {
        return Boolean(this.get('streams')['hlsdrm']);
    }

    /**
     * Check if stream is behind paywall
     */
    hasAccess() {
        return !!this.get('access');
    }

    /**
     * Check wheter give stream contains mock data to allow embedding player without asset id
     */
    isMock() {
        return this.get('type') === 'mock';
    }

    /**
     * Check if stream is available only in certain areas
     */
    isGeoblocked() {
        return this.hasProperty('geoblocked');
    }

    isLive() {
        return this.get('type') === 'live';
    }

    wasLive() {
        return this.get('type') === 'wasLive';
    }

    isActive() {
        return this.get('status') === 'active' && !this.isPast();
    }

    isFuture() {
        const flightTimes = this.get('flightTimes');
        if (!flightTimes?.start) return false;
        return flightTimes.start > ServerTime.getTime();
    }

    isPast() {
        const flightTimes = this.get('flightTimes');
        if (!flightTimes?.end) return false;
        return flightTimes.end < ServerTime.getTime();
    }

    hasPlaylist() {
        return this.get('playlist').length > 0;
    }

    isDisabledNextVideo() {
        return !!this.get('disableNextVideo');
    }
    /**
     * Streams might not be available while asset is created
     */
    hasPlayableSource() {
        if (this.getTimeToStart() > 0 || !this.isActive()) {
            return false;
        }

        return true;
    }

    getUrl(streamType: keyof Stream['streams']) {
        const isOnHttp = window.location.protocol
            .toLowerCase()
            .startsWith('http:');
        const type = streamType.toLowerCase() as typeof streamType;

        let streamUrl = this.get('streams')[type];

        if (utils.device.isIE() && isOnHttp && typeof streamUrl === 'string') {
            streamUrl = streamUrl.replace('https', 'http');
        }

        const category = this.getCategory();
        const vendor = this.getVendor();

        if (
            (vendor === 'ab' || vendor === 'svd') &&
            category &&
            streamType === 'mp4' &&
            Array.isArray(streamUrl)
        ) {
            const prependUrl = category.getAcastPrependUrl();
            if (prependUrl && streamUrl[0]?.startsWith(prependUrl)) {
                streamUrl[0] = `https://${streamUrl[0].replace(prependUrl, '')}`;
            }
        }

        return streamUrl;
    }

    getPoster(): string {
        return this.get('images').main!;
    }

    getAllPreviewsUrls(): Record<string, string> {
        return this.get('videoPreviews') || {};
    }

    /**
     * Returns preview video URL
     */
    getVideoPreviewUrl(metadataKey: string) {
        return this.get('videoPreviews')?.[metadataKey];
    }

    /**
     * Get chunk duration of live video
     */
    getChunkDuration(): number {
        return this.get('chunkDuration');
    }

    /**
     * Get snapshots url
     */
    getSnapshots(): string {
        return `${
            config.api.thumbnailsUrl + this.getVendor()
        }/assets/${this.getId()}`;
    }

    /**
     * Not every asset has snapshot (lives, wasLives)
     */
    hasSnapshots() {
        return this.get('images').snapshots !== null;
    }

    /**
     * Get captions associated with stream
     */
    getCaptions(): Captions[] {
        return this.get('captions') || [];
    }

    /**
     * Check if mp4 streams are available
     */
    hasMp4Streams() {
        return this.get('streams').mp4.length > 0;
    }

    /**
     * Check if asset is published
     * Method relay on server time to avoid timezone differences
     */
    getTimeToStart() {
        return (this.get('flightTimes')!.start! - ServerTime.getTime()) / 1000;
    }

    /**
     * Asset can be virually sliced into small chunks
     * These are only indicators and do not slice stream
     */
    getPlaybackTime(position: 'begin' | 'end'): number | null {
        const playback = this.get('playback');

        if (position === 'begin') {
            if (this.get('playAhead') > 0) {
                return this.get('playAhead');
            }

            return playback && playback.begin! > 0 ? playback.begin : null;
        }

        if (position === 'end' && playback && playback.end! > 0) {
            return playback.end;
        }

        return null;
    }

    /**
     * External id
     * Used for JW Player statistics
     */
    getExternalId(): string | undefined {
        return this.get('externalId');
    }

    /**
     * Check wethever stream has ads disabled
     */
    hasAdsDisabled(): boolean {
        return this.get('showAds') === false;
    }

    isAudio(): boolean {
        return this.get('mediaType') === 'audio';
    }

    hasTextSpeech(): boolean {
        return this.get('tts');
    }

    /**
     * Check if stream is podcast
     */
    isPodcast(): boolean {
        return this.get('isPodcast') === true;
    }

    getVmapOverrides() {
        const qs = new URLSearchParams(window.location.search);
        if (qs.has(OVERRIDE_SLOT_TAG_ID) || qs.has(OVERRIDE_SLOT_PARAM)) {
            const overrides = [...qs.entries()]
                .filter(([key]) =>
                    [OVERRIDE_SLOT_PARAM, OVERRIDE_SLOT_TAG_ID].includes(key),
                )
                .map(([, value]) => value);

            return encodeURIComponent(String(overrides));
        }

        return undefined;
    }

    /**
     * Get VMAP API URL for ads schedule
     */
    getVmapApiUrl(params: Record<string, string> = {}): string {
        let supplyType = 'web_desktop';

        if (device.isMobile()) {
            supplyType = 'web_phone';
        } else if (device.isTablet()) {
            supplyType = 'web_tablet';
        }

        const queryParams = new URLSearchParams({
            gdpr: '__gdpr__',
            gdpr_consent: '__gdpr_consent__',
            ...params,
            supplyType,
        });

        const overrides = this.getVmapOverrides();
        if (overrides) {
            queryParams.set('overrides', overrides);
        }

        return `${
            config.api.vmapUrl
        }/${this.getVendor()}/${this.getId()}?${queryParams.toString()}`;
    }
}
